import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { objWall, objWallInsp } from "@/data/walls";

export const useWallsStore = defineStore("walls", () => {
  let pageData = ref({});

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      isSuccess = await getInspLinkDataObject(brkey, inspectionId);
    }
    return isSuccess;
  };

  const getWallsObject = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_WALLS;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Walls", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspLinkObject = (wallValues) => {
    return {
      // identifiers
      brkey: wallValues.brkey,
      inspectionId: wallValues.inspectionId,

      // comments
      inspectionComments: wallValues.inspectionComments,

      // page values
      anchorageCondRate: wallValues.anchorageCondRate,
      backfillCondRate: wallValues.backfillCondRate,
      drainageCondRate: wallValues.drainageCondRate,
      foundCondRate: wallValues.foundCondRate,
      panelCondRate: wallValues.panelCondRate,
      parapetsCondRate: wallValues.parapetsCondRate,
      postCondRate: wallValues.postCondRate,
      strRating: wallValues.strRating,
      wallCondRate: wallValues.wallCondRate,
    };
  };

  const convertToSaveInspModuleObject = (wallValues, structure) => {
    // If there is no existing wall object, we need to create one
    if (!structure?.structureWall) {
      let newWall = clone(objWall);
      newWall.brkey = structure.structureBridge.brkey;
      structure.structureWall = clone(newWall);
    }

    // If there is no existing wall inspection object, we need to create one
    if (!structure?.inspectionWall) {
      let newWallInsp = clone(objWallInsp);
      newWallInsp.brkey = structure.structureBridge.brkey;
      newWallInsp.inspectionId = structure.inspection.inspectionId;
      structure.inspectionWall = clone(newWallInsp);
    }

    structure.inspectionWall.anchorageCondRate = wallValues.anchorageCondRate;
    structure.inspectionWall.backfillCondRate = wallValues.backfillCondRate;
    structure.inspectionWall.drainageCondRate = wallValues.drainageCondRate;
    structure.inspectionWall.foundCondRate = wallValues.foundCondRate;
    structure.inspectionWall.panelCondRate = wallValues.panelCondRate;
    structure.inspectionWall.parapetsCondRate = wallValues.parapetsCondRate;
    structure.inspectionWall.postCondRate = wallValues.postCondRate;
    structure.inspectionWall.strRating = wallValues.strRating;
    structure.inspectionWall.wallCondRate = wallValues.wallCondRate;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    //clear idb object
    pageData.value = {};
    let isSuccess = false;
    let url = INSPECTION_LINKS_ENDPOINT.GET_WALLS.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((resp) => {
        isSuccess = true;
        convertToInspLinkObject(resp.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
    return isSuccess;
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      anchorageCondRate: structure?.inspectionWall?.anchorageCondRate,
      backFillMaterial1: structure?.structureWall?.backFillMaterial1,
      backFillMaterial2: structure?.structureWall?.backFillMaterial2,
      backfillCondRate: structure?.inspectionWall?.backfillCondRate,
      drainageCondRate: structure?.inspectionWall?.drainageCondRate,
      foundCondRate: structure?.inspectionWall?.foundCondRate,
      panelCondRate: structure?.inspectionWall?.panelCondRate,
      parapetsCondRate: structure?.inspectionWall?.parapetsCondRate,
      postCondRate: structure?.inspectionWall?.postCondRate,
      strRating: structure?.inspectionWall?.strRating,
      wallCondRate: structure?.inspectionWall?.wallCondRate,
    };
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      // identifiers
      brkey: response.brkey,
      inspectionId: response.inspectionId,

      // comments
      inspectionComments: response.inspectionComments,

      // page values
      anchorageCondRate: response.anchorageCondRate,
      backFillMaterial1: response.backFillMaterial1,
      backFillMaterial2: response.backFillMaterial2,
      backfillCondRate: response.backfillCondRate,
      drainageCondRate: response.drainageCondRate,
      foundCondRate: response.foundCondRate,
      panelCondRate: response.panelCondRate,
      parapetsCondRate: response.parapetsCondRate,
      postCondRate: response.postCondRate,
      strRating: response.strRating,
      wallCondRate: response.wallCondRate,
    };
  };

  return {
    getWallsObject,
    saveData,
    loadDataObject,
  };
});
